import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimation } from 'framer-motion';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, MapPin, Users, CreditCard, Vote, Car, Sparkles } from 'lucide-react';

const ONBOARDING_SLIDES = [
  {
    id: 'welcome',
    icon: '✈️',
    title: 'Bem-vindo ao ViajeJunto!',
    description: 'Organize viagens em grupo de forma simples, justa e divertida.',
    color: 'from-orange-400 to-pink-500',
    illustration: '🌴🏖️🌊',
  },
  {
    id: 'itinerary',
    icon: MapPin,
    title: 'Roteiro Completo',
    description: 'Crie roteiros dia a dia com horários, locais e custos. Tudo organizado!',
    color: 'from-blue-400 to-cyan-500',
    illustration: '📍🗺️🧭',
  },
  {
    id: 'payments',
    icon: CreditCard,
    title: 'Divisão Justa',
    description: 'Divida despesas automaticamente. Cada um paga sua parte, sem confusão!',
    color: 'from-green-400 to-emerald-500',
    illustration: '💰💳✅',
  },
  {
    id: 'votes',
    icon: Vote,
    title: 'Decisões em Grupo',
    description: 'Vote em restaurantes, passeios e mais. Democracia na viagem!',
    color: 'from-purple-400 to-violet-500',
    illustration: '🗳️👍🎯',
  },
  {
    id: 'carpools',
    icon: Car,
    title: 'Caronas Organizadas',
    description: 'Coordene caronas, divida combustível e chegue junto!',
    color: 'from-amber-400 to-orange-500',
    illustration: '🚗🛣️🎉',
  },
  {
    id: 'ready',
    icon: Sparkles,
    title: 'Pronto para Viajar!',
    description: 'Crie sua primeira viagem e convide os amigos. Bora?',
    color: 'from-pink-400 to-rose-500',
    illustration: '🚀🌟🎊',
  },
];

const SwipeCard = ({ slide, isActive, direction }) => {
  const Icon = typeof slide.icon === 'string' ? null : slide.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: direction === 'right' ? 300 : -300 }}
      animate={{ opacity: isActive ? 1 : 0, x: isActive ? 0 : direction === 'right' ? -300 : 300 }}
      exit={{ opacity: 0, x: direction === 'right' ? -300 : 300 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`absolute inset-0 flex flex-col items-center justify-center p-8 bg-gradient-to-br ${slide.color} rounded-3xl text-white`}
    >
      {/* Floating Emojis Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {slide.illustration.split('').filter(c => c.trim()).map((emoji, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 100 }}
            animate={{ 
              opacity: [0, 0.3, 0],
              y: [-20, -100],
              x: Math.sin(i) * 50
            }}
            transition={{ 
              duration: 3,
              delay: i * 0.5,
              repeat: Infinity,
              repeatDelay: 2
            }}
            className="absolute text-4xl"
            style={{ 
              left: `${20 + (i * 25)}%`,
              bottom: '10%'
            }}
          >
            {emoji}
          </motion.span>
        ))}
      </div>

      {/* Content */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6"
      >
        {Icon ? (
          <Icon className="w-12 h-12 text-white" />
        ) : (
          <span className="text-5xl">{slide.icon}</span>
        )}
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-2xl sm:text-3xl font-bold text-center mb-4"
      >
        {slide.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-lg text-white/90 text-center max-w-sm"
      >
        {slide.description}
      </motion.p>
    </motion.div>
  );
};

export const OnboardingCards = ({ onComplete, onSkip }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState('right');
  const containerRef = useRef(null);

  const handleNext = () => {
    if (currentIndex < ONBOARDING_SLIDES.length - 1) {
      setDirection('right');
      setCurrentIndex(currentIndex + 1);
    } else {
      onComplete?.();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setDirection('left');
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleDotClick = (index) => {
    setDirection(index > currentIndex ? 'right' : 'left');
    setCurrentIndex(index);
  };

  const isLastSlide = currentIndex === ONBOARDING_SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-md"
      >
        {/* Skip Button */}
        <Button
          variant="ghost"
          onClick={onSkip}
          className="absolute -top-12 right-0 text-white/80 hover:text-white z-10"
        >
          Pular
        </Button>

        {/* Card Container */}
        <div
          ref={containerRef}
          className="relative h-[450px] sm:h-[500px] overflow-hidden rounded-3xl shadow-2xl"
        >
          <AnimatePresence mode="wait">
            <SwipeCard
              key={currentIndex}
              slide={ONBOARDING_SLIDES[currentIndex]}
              isActive={true}
              direction={direction}
            />
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>

          {/* Dots */}
          <div className="flex gap-2">
            {ONBOARDING_SLIDES.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-white w-8'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            className={`h-12 px-6 rounded-full font-semibold ${
              isLastSlide
                ? 'bg-white text-orange-500 hover:bg-white/90'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            {isLastSlide ? (
              <>
                Começar! <Sparkles className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Próximo <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingCards;
